import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { usePageMeta, eur } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import AffiliateTab from "@/pages/coach/offerings/AffiliateTab";
import {
  ChevronLeft, Plus, Pencil, Trash2, Star, Store, Package, Boxes, Image as ImageIcon,
  CalendarClock, Users,
} from "lucide-react";

const OFFER_TYPES = ["one_on_one", "group_online", "group_inperson", "retreat", "workshop", "package", "custom"];
const DURATIONS = [15, 30, 45, 60, 90];

function toCents(v) {
  const n = parseFloat(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

export default function Offerings() {
  const { user } = useAuth();
  const t = useT();
  usePageMeta(t("off.meta_title"));
  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  return (
    <div data-testid="coach-offerings-page">
      <Seo title={t("off.seo")} noindex />
      <Link to="/app/coach" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="offerings-back">
        <ChevronLeft className="h-4 w-4" /> {t("off.back")}
      </Link>
      <PageHeader eyebrow={t("off.eyebrow")} title={t("off.title")} subtitle={t("off.subtitle")} />
      <Tabs defaultValue="offerings">
        <TabsList className="mb-4 flex h-auto w-full flex-wrap justify-start gap-1 bg-secondary/60 p-1">
          <TabsTrigger value="offerings" data-testid="off-tab-offerings">{t("off.tab_offerings")}</TabsTrigger>
          <TabsTrigger value="products" data-testid="off-tab-products">{t("off.tab_products")}</TabsTrigger>
          <TabsTrigger value="affiliate" data-testid="off-tab-affiliate">{t("aff.tab_label")}</TabsTrigger>
        </TabsList>
        <TabsContent value="offerings"><OfferingsTab t={t} /></TabsContent>
        <TabsContent value="products"><ProductsTab t={t} /></TabsContent>
        <TabsContent value="affiliate"><AffiliateTab t={t} /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ───────────────────────── Offerings ───────────────────────── */

function OfferingsTab({ t }) {
  const [rows, setRows] = useState(null);
  const [hideInactive, setHideInactive] = useState(true);
  const [editing, setEditing] = useState(null);
  const [bundleOpen, setBundleOpen] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/coach/offerings/mine"); setRows(Array.isArray(data) ? data : []); }
    catch { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (o) => {
    if (!window.confirm(t("off.delete_confirm"))) return;
    try { await api.delete(`/coach/offerings/${o.id}`); toast.success(t("off.deleted")); await load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const typeLabel = (o) => (OFFER_TYPES.includes(o.type) ? t(`off.type.${o.type}`) : (o.type_label || o.type));
  const visible = (rows || []).filter((o) => (hideInactive ? o.active !== false : true));

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <Switch checked={hideInactive} onCheckedChange={setHideInactive} data-testid="off-hide-inactive" />
          {t("off.hide_inactive")}
        </label>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-1.5 rounded-full" onClick={() => setBundleOpen(true)} data-testid="off-new-bundle">
            <Boxes className="h-4 w-4" /> {t("off.new_bundle")}
          </Button>
          <Button className="gap-1.5 rounded-full" onClick={() => setEditing({})} data-testid="off-new">
            <Plus className="h-4 w-4" /> {t("off.new")}
          </Button>
        </div>
      </div>

      {rows === null ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>
      ) : visible.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><Store className="h-6 w-6" /></div>
          <p className="mt-4 font-display text-lg text-foreground">{t("off.empty_title")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("off.empty_sub")}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((o) => (
            <div key={o.id} data-testid={`offering-${o.id}`} className={`flex items-center gap-3 rounded-xl border border-border bg-card p-3 ${o.active === false ? "opacity-60" : ""}`}>
              {o.cover_image_url ? (
                <img src={o.cover_image_url} alt="" className="hidden h-12 w-20 shrink-0 rounded-lg object-cover sm:block" />
              ) : null}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  {o.is_standard && <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-500" />}
                  <p className="truncate font-semibold text-foreground">{o.title}</p>
                  {o.active === false
                    ? <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">{t("off.inactive")}</span>
                    : <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">{t("off.active")}</span>}
                  <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">{o.is_online !== false ? t("rw3.off_online") : t("rw3.off_inperson")}</span>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {typeLabel(o)} · {eur(o.price_cents)}{o.duration_min ? ` · ${o.duration_min} ${t("off.min")}` : ""}{o.location ? ` · 📍 ${o.location}` : ""}
                </p>
              </div>
              <div className="flex shrink-0 gap-1">
                <Button size="icon" variant="ghost" className="h-9 w-9 rounded-full" onClick={() => setEditing(o)} data-testid={`offering-edit-${o.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="h-9 w-9 rounded-full text-rose-600" onClick={() => remove(o)} data-testid={`offering-del-${o.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <OfferingDialog t={t} initial={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); }} />}
      {bundleOpen && <BundleDialog t={t} onClose={() => setBundleOpen(false)} onSaved={async () => { setBundleOpen(false); await load(); }} />}
    </div>
  );
}

function OfferingDialog({ t, initial, onClose, onSaved }) {
  const isEdit = Boolean(initial?.id);
  const [form, setForm] = useState({
    type: initial.type || "one_on_one",
    custom_type_label: initial.custom_type_label || "",
    title: initial.title || "",
    description: initial.description || "",
    duration_min: initial.duration_min || 60,
    price_eur: initial.price_cents != null ? String(initial.price_cents / 100) : "50",
    is_online: initial.is_online !== false,
    location: initial.location || "online",
    schedule_start: initial.schedule_start || "",
    schedule_end: initial.schedule_end || "",
    capacity: initial.capacity || "",
    package_sessions: initial.package_sessions || "",
    cover_image_url: initial.cover_image_url || null,
    is_standard: Boolean(initial.is_standard),
    bookable_via_weekly_availability: Boolean(initial.bookable_via_weekly_availability),
    active: initial.active !== false,
  });
  const [saving, setSaving] = useState(false);
  const [showLib, setShowLib] = useState(false);
  const [templates, setTemplates] = useState(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const openLib = async () => {
    setShowLib((v) => !v);
    if (templates === null) {
      try { const { data } = await api.get("/coach/offering-cover-templates"); setTemplates(data?.templates || []); }
      catch { setTemplates([]); }
    }
  };

  const save = async () => {
    const cents = toCents(form.price_eur);
    if (form.title.trim().length < 2) { toast.error(t("off.f_title")); return; }
    if (cents < 1499) { toast.error(t("off.min_price")); return; }
    if (form.type === "custom" && !form.custom_type_label.trim()) { toast.error(t("off.f_custom_label")); return; }
    if (form.type === "retreat" && (!form.schedule_start || !form.schedule_end)) { toast.error(t("off.f_end")); return; }
    setSaving(true);
    const body = {
      type: form.type,
      title: form.title.trim(),
      description: form.description.trim(),
      price_cents: cents,
      currency: "eur",
      is_online: form.is_online,
      location: form.location.trim(),
      is_standard: form.is_standard,
      bookable_via_weekly_availability: form.bookable_via_weekly_availability,
      cover_image_url: form.cover_image_url || null,
    };
    if (form.type === "custom") body.custom_type_label = form.custom_type_label.trim();
    if (form.type === "one_on_one") body.duration_min = form.duration_min;
    if (form.type === "retreat" || form.type === "workshop" || form.type.startsWith("group_")) {
      body.schedule_start = form.schedule_start || null;
      if (form.type === "retreat") body.schedule_end = form.schedule_end || null;
      body.capacity = parseInt(form.capacity) || null;
    }
    if (form.type === "package") body.package_sessions = parseInt(form.package_sessions) || null;
    try {
      if (isEdit) await api.patch(`/coach/offerings/${initial.id}`, { ...body, active: form.active });
      else await api.post("/coach/offerings", body);
      toast.success(t("off.saved"));
      onSaved();
    } catch (err) { toast.error(apiError(err, t("off.save_failed"))); }
    finally { setSaving(false); }
  };

  const showSchedule = form.type === "retreat" || form.type === "workshop" || form.type.startsWith("group_");

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="offering-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? t("off.dialog_edit") : t("off.dialog_new")}</DialogTitle>
          <DialogDescription>{t("off.commission_hint")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field label={t("off.f_type")}>
            <div className="flex flex-wrap gap-2">
              {OFFER_TYPES.map((tp) => (
                <button key={tp} type="button" onClick={() => set({ type: tp })} data-testid={`off-type-${tp}`}
                  className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${form.type === tp ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>
                  {t(`off.type.${tp}`)}
                </button>
              ))}
            </div>
          </Field>

          {form.type === "custom" && (
            <Field label={t("off.f_custom_label")}>
              <Input value={form.custom_type_label} onChange={(e) => set({ custom_type_label: e.target.value })} placeholder={t("off.f_custom_ph")} data-testid="off-custom-label" />
            </Field>
          )}

          <Field label={t("off.f_title")}>
            <Input value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder={t("off.f_title_ph")} data-testid="off-title" />
          </Field>

          <Field label={t("off.f_desc")}>
            <Textarea className="min-h-[80px]" value={form.description} onChange={(e) => set({ description: e.target.value })} placeholder={t("off.f_desc_ph")} data-testid="off-desc" />
          </Field>

          {form.type === "one_on_one" && (
            <Field label={t("off.f_duration")}>
              <div className="flex flex-wrap gap-2">
                {DURATIONS.map((d) => (
                  <button key={d} type="button" onClick={() => set({ duration_min: d })} data-testid={`off-dur-${d}`}
                    className={`rounded-full border px-3 py-1.5 text-sm ${form.duration_min === d ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>
                    {d}
                  </button>
                ))}
              </div>
            </Field>
          )}

          <Field label={t("off.f_price")}>
            <Input type="text" inputMode="decimal" value={form.price_eur} onChange={(e) => set({ price_eur: e.target.value })} className="max-w-[180px]" data-testid="off-price" />
          </Field>

          <Field label={t("off.f_modality")}>
            <div className="flex gap-2">
              <button type="button" onClick={() => set({ is_online: true, location: form.location || "online" })} data-testid="off-modality-online"
                className={`rounded-full border px-3 py-1.5 text-sm ${form.is_online ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>
                {t("off.f_online")}
              </button>
              <button type="button" onClick={() => set({ is_online: false, location: form.location && form.location.toLowerCase() !== "online" ? form.location : "" })} data-testid="off-modality-inperson"
                className={`rounded-full border px-3 py-1.5 text-sm ${!form.is_online ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>
                {t("off.f_inperson")}
              </button>
            </div>
          </Field>

          <Field label={form.is_online ? t("off.f_location_note") : t("off.f_location")}>
            <Input value={form.location} onChange={(e) => set({ location: e.target.value })} placeholder={form.is_online ? t("off.f_location_online_ph") : t("off.f_location_ph")} data-testid="off-location" />
            {form.is_online && <p className="mt-1 text-xs italic text-muted-foreground">{t("off.online_hint")}</p>}
          </Field>

          {showSchedule && (
            <>
              <Field label={t("off.f_start")}>
                <Input value={form.schedule_start} onChange={(e) => set({ schedule_start: e.target.value })} placeholder={t("off.f_start_ph")} data-testid="off-start" />
              </Field>
              {form.type === "retreat" && (
                <Field label={t("off.f_end")}>
                  <Input value={form.schedule_end} onChange={(e) => set({ schedule_end: e.target.value })} placeholder={t("off.f_end_ph")} data-testid="off-end" />
                </Field>
              )}
              <Field label={t("off.f_capacity")}>
                <Input type="number" min="1" value={form.capacity} onChange={(e) => set({ capacity: e.target.value })} className="max-w-[140px]" data-testid="off-capacity" />
              </Field>
            </>
          )}

          {form.type === "package" && (
            <Field label={t("off.f_sessions")}>
              <Input type="number" min="2" value={form.package_sessions} onChange={(e) => set({ package_sessions: e.target.value })} className="max-w-[140px]" data-testid="off-sessions" />
            </Field>
          )}

          {/* Cover image */}
          <Field label={t("off.cover")}>
            <p className="mb-2 text-xs text-muted-foreground">{t("off.cover_hint")}</p>
            {form.cover_image_url ? (
              <div className="space-y-2">
                <img src={form.cover_image_url} alt="" className="aspect-video w-full max-w-sm rounded-lg object-cover" />
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={openLib} data-testid="off-cover-library">{showLib ? t("off.cover_hide") : t("off.cover_library")}</Button>
                  <Button type="button" size="sm" variant="outline" className="rounded-full text-rose-600" onClick={() => set({ cover_image_url: null })} data-testid="off-cover-remove">{t("off.cover_remove")}</Button>
                </div>
              </div>
            ) : (
              <Button type="button" size="sm" variant="outline" className="gap-1.5 rounded-full" onClick={openLib} data-testid="off-cover-library">
                <ImageIcon className="h-4 w-4" /> {showLib ? t("off.cover_hide") : t("off.cover_library")}
              </Button>
            )}
            {showLib && (
              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                {templates === null ? [0, 1, 2, 3].map((i) => <Skeleton key={i} className="aspect-video rounded-lg" />)
                  : templates.map((tpl) => (
                    <button key={tpl.id} type="button" onClick={() => { set({ cover_image_url: tpl.url }); setShowLib(false); }} data-testid={`off-cover-tpl-${tpl.id}`}
                      className="overflow-hidden rounded-lg border border-border transition-transform hover:scale-[1.02]">
                      <img src={tpl.url} alt={tpl.title} className="aspect-video w-full object-cover" />
                    </button>
                  ))}
              </div>
            )}
          </Field>

          {/* Toggles */}
          <ToggleRow icon={Star} title={t("off.toggle_standard")} hint={t("off.toggle_standard_hint")} checked={form.is_standard} onChange={(v) => set({ is_standard: v })} testId="off-toggle-standard" />
          <ToggleRow icon={CalendarClock} title={t("off.toggle_weekly")} hint={t("off.toggle_weekly_hint")} checked={form.bookable_via_weekly_availability} onChange={(v) => set({ bookable_via_weekly_availability: v })} testId="off-toggle-weekly" />
          {isEdit && (
            <ToggleRow icon={Store} title={t("off.active")} hint="" checked={form.active} onChange={(v) => set({ active: v })} testId="off-toggle-active" />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose} data-testid="off-cancel">{t("common.cancel")}</Button>
          <Button className="rounded-full" onClick={save} disabled={saving} data-testid="off-save">{saving ? t("common.saving") : t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BundleDialog({ t, onClose, onSaved }) {
  const [eligible, setEligible] = useState(null);
  const [selected, setSelected] = useState([]);
  const [discount, setDiscount] = useState("10");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/coach/offerings/eligible-for-bundle").then(({ data }) => setEligible(Array.isArray(data) ? data : [])).catch(() => setEligible([]));
  }, []);

  useEffect(() => {
    if (selected.length < 2) { setPreview(null); return; }
    const id = setTimeout(() => {
      api.post("/coach/offerings/bundle-preview", { bundle_items: selected, bundle_discount_pct: parseFloat(discount) || 0 })
        .then(({ data }) => setPreview(data)).catch(() => setPreview(null));
    }, 250);
    return () => clearTimeout(id);
  }, [selected, discount]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const save = async () => {
    if (selected.length < 2) { toast.error(t("off.bundle_need2")); return; }
    if (title.trim().length < 2) { toast.error(t("off.f_title")); return; }
    setSaving(true);
    try {
      await api.post("/coach/offerings", {
        type: "bundle",
        title: title.trim(),
        description: description.trim(),
        price_cents: preview?.effective_cents ?? 1499,
        currency: "eur",
        bundle_items: selected,
        bundle_discount_pct: parseFloat(discount) || 0,
      });
      toast.success(t("off.bundle_saved"));
      onSaved();
    } catch (err) { toast.error(apiError(err, t("off.save_failed"))); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="bundle-dialog">
        <DialogHeader>
          <DialogTitle>{t("off.bundle_title")}</DialogTitle>
          <DialogDescription>{t("off.commission_hint")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field label={t("off.f_title")}>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("off.f_title_ph")} data-testid="bundle-title" />
          </Field>
          <Field label={t("off.f_desc")}>
            <Textarea className="min-h-[60px]" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t("off.f_desc_ph")} data-testid="bundle-desc" />
          </Field>
          <Field label={t("off.bundle_pick")}>
            {eligible === null ? (
              <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-12 rounded-lg" />)}</div>
            ) : eligible.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("off.bundle_none")}</p>
            ) : (
              <div className="space-y-2">
                {eligible.map((o) => (
                  <label key={o.id} data-testid={`bundle-item-${o.id}`} className={`flex cursor-pointer items-center gap-3 rounded-lg border p-2.5 ${selected.includes(o.id) ? "border-primary bg-primary/5" : "border-border"}`}>
                    <input type="checkbox" checked={selected.includes(o.id)} onChange={() => toggle(o.id)} className="h-4 w-4 accent-primary" />
                    <span className="min-w-0 flex-1 truncate text-sm text-foreground">{o.title}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">{eur(o.price_cents)}</span>
                  </label>
                ))}
              </div>
            )}
          </Field>
          <Field label={t("off.bundle_discount")}>
            <Input type="number" min="0" max="90" value={discount} onChange={(e) => setDiscount(e.target.value)} className="max-w-[120px]" data-testid="bundle-discount" />
          </Field>
          {preview && (
            <div className="rounded-xl border border-border bg-card p-3 text-sm" data-testid="bundle-preview">
              <Row label={t("off.bundle_total")} value={eur(preview.total_cents)} />
              <Row label={t("off.bundle_effective")} value={eur(preview.effective_cents)} strong />
              <Row label={t("off.bundle_savings")} value={eur(preview.savings_cents)} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose} data-testid="bundle-cancel">{t("common.cancel")}</Button>
          <Button className="rounded-full" onClick={save} disabled={saving || selected.length < 2} data-testid="bundle-save">{saving ? t("common.saving") : t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ───────────────────────── Products ───────────────────────── */

function ProductsTab({ t }) {
  const [rows, setRows] = useState(null);
  const [hideInactive, setHideInactive] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/coach/products/mine"); setRows(Array.isArray(data) ? data : []); }
    catch { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (p) => {
    if (!window.confirm(t("off.p_delete_confirm"))) return;
    try { await api.delete(`/coach/products/${p.id}`); toast.success(t("off.p_deleted")); await load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const visible = (rows || []).filter((p) => (hideInactive ? p.active !== false : true));

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <Switch checked={hideInactive} onCheckedChange={setHideInactive} data-testid="prod-hide-inactive" />
          {t("off.hide_inactive")}
        </label>
        <Button className="gap-1.5 rounded-full" onClick={() => setEditing({})} data-testid="prod-new">
          <Plus className="h-4 w-4" /> {t("off.p_new")}
        </Button>
      </div>

      {rows === null ? (
        <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>
      ) : visible.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><Package className="h-6 w-6" /></div>
          <p className="mt-4 font-display text-lg text-foreground">{t("off.p_empty_title")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("off.p_empty_sub")}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((p) => (
            <div key={p.id} data-testid={`product-${p.id}`} className={`flex items-center gap-3 rounded-xl border border-border bg-card p-3 ${p.active === false ? "opacity-60" : ""}`}>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate font-semibold text-foreground">{p.name}</p>
                  {p.is_group && <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-muted-foreground"><Users className="h-3 w-3" /> {p.capacity}</span>}
                  {p.active === false && <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">{t("off.inactive")}</span>}
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {p.duration_min} {t("off.min")}
                  {(p.payment_modes || []).includes("credits") && p.price_credits > 0 ? ` · ${p.price_credits} Credits` : ""}
                  {(p.payment_modes || []).includes("stripe") && p.price_cents > 0 ? ` · ${eur(p.price_cents)}` : ""}
                </p>
              </div>
              <div className="flex shrink-0 gap-1">
                <Button size="icon" variant="ghost" className="h-9 w-9 rounded-full" onClick={() => setEditing(p)} data-testid={`product-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="h-9 w-9 rounded-full text-rose-600" onClick={() => remove(p)} data-testid={`product-del-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <ProductDialog t={t} initial={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); }} />}
    </div>
  );
}

function ProductDialog({ t, initial, onClose, onSaved }) {
  const isEdit = Boolean(initial?.id);
  const [form, setForm] = useState({
    name: initial.name || "",
    description: initial.description || "",
    duration_min: String(initial.duration_min ?? 60),
    capacity: String(initial.capacity ?? 1),
    is_group: Boolean(initial.is_group),
    accept_credits: initial.payment_modes ? initial.payment_modes.includes("credits") : true,
    price_credits: String(initial.price_credits ?? 0),
    accept_stripe: initial.payment_modes ? initial.payment_modes.includes("stripe") : false,
    price_eur: initial.price_cents ? (initial.price_cents / 100).toFixed(2) : "0",
  });
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (!form.name.trim()) { toast.error(t("off.p_name_required")); return; }
    const modes = [...(form.accept_credits ? ["credits"] : []), ...(form.accept_stripe ? ["stripe"] : [])];
    if (modes.length === 0) { toast.error(t("off.p_payment_required")); return; }
    setSaving(true);
    const body = {
      name: form.name.trim(),
      description: form.description.trim(),
      duration_min: Math.max(15, parseInt(form.duration_min) || 60),
      price_credits: Math.max(0, parseInt(form.price_credits) || 0),
      price_cents: Math.max(0, toCents(form.price_eur)),
      payment_modes: modes,
      capacity: Math.max(1, parseInt(form.capacity) || 1),
      is_group: form.is_group || (parseInt(form.capacity) || 1) > 1,
    };
    try {
      if (isEdit) await api.patch(`/coach/products/${initial.id}`, body);
      else await api.post("/coach/products", body);
      toast.success(t("off.p_saved"));
      onSaved();
    } catch (err) { toast.error(apiError(err, t("off.save_failed"))); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? t("off.p_dialog_edit") : t("off.p_dialog_new")}</DialogTitle>
          <DialogDescription>{t("off.p_empty_sub")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field label={t("off.p_name")}>
            <Input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder={t("off.p_name_ph")} data-testid="prod-name" />
          </Field>
          <Field label={t("off.p_desc")}>
            <Textarea className="min-h-[60px]" value={form.description} onChange={(e) => set({ description: e.target.value })} data-testid="prod-desc" />
          </Field>
          <div className="flex gap-3">
            <Field label={t("off.p_duration")} className="flex-1">
              <Input type="number" min="15" value={form.duration_min} onChange={(e) => set({ duration_min: e.target.value })} data-testid="prod-duration" />
            </Field>
            <Field label={t("off.p_capacity")} className="flex-1">
              <Input type="number" min="1" value={form.capacity} onChange={(e) => set({ capacity: e.target.value, is_group: (parseInt(e.target.value) || 1) > 1 ? true : form.is_group })} data-testid="prod-capacity" />
            </Field>
          </div>
          <ToggleRow icon={Users} title={t("off.p_group")} hint="" checked={form.is_group} onChange={(v) => set({ is_group: v })} testId="prod-group" />

          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("off.p_payment")}</p>
          <ToggleRow title={t("off.p_accept_credits")} hint="" checked={form.accept_credits} onChange={(v) => set({ accept_credits: v })} testId="prod-accept-credits" />
          {form.accept_credits && (
            <Field label={t("off.p_price_credits")}>
              <Input type="number" min="0" value={form.price_credits} onChange={(e) => set({ price_credits: e.target.value })} className="max-w-[160px]" data-testid="prod-price-credits" />
            </Field>
          )}
          <ToggleRow title={t("off.p_accept_stripe")} hint="" checked={form.accept_stripe} onChange={(v) => set({ accept_stripe: v })} testId="prod-accept-stripe" />
          {form.accept_stripe && (
            <Field label={t("off.p_price_eur")}>
              <Input type="text" inputMode="decimal" value={form.price_eur} onChange={(e) => set({ price_eur: e.target.value })} className="max-w-[160px]" data-testid="prod-price-eur" />
            </Field>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose} data-testid="prod-cancel">{t("common.cancel")}</Button>
          <Button className="rounded-full" onClick={save} disabled={saving} data-testid="prod-save">{saving ? t("common.saving") : t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ───────────────────────── Shared bits ───────────────────────── */

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

function ToggleRow({ icon: Icon, title, hint, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-border p-3">
      <div className="flex min-w-0 items-center gap-2">
        {Icon && <Icon className={`h-4 w-4 shrink-0 ${checked ? "text-primary" : "text-muted-foreground"}`} />}
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">{title}</p>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}

function Row({ label, value, strong }) {
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-muted-foreground">{label}</span>
      <span className={strong ? "font-display text-base font-semibold text-foreground" : "text-foreground"}>{value}</span>
    </div>
  );
}
